import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import api, { formatError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Plus, Trash, Clock, Image as ImageIcon, X } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Horas() {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const [registros, setRegistros] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({
    cliente_id: "",
    data: new Date().toISOString().split("T")[0],
    hora_inicio: "08:00",
    hora_fim: "17:00",
    almuerzo_horas: 1.5,
    observacoes: "",
  });
  const [photoFiles, setPhotoFiles] = useState([]); // [{name, base64}]

  const load = async () => {
    setLoading(true);
    try {
      const [h, c] = await Promise.all([api.get("/horas"), api.get("/clientes")]);
      setRegistros(h.data);
      setClientes(c.data);
      if (isAdmin) {
        const f = await api.get("/funcionarios");
        setFuncionarios(f.data);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/horas", { ...form, almuerzo_horas: parseFloat(form.almuerzo_horas) || 0 });

      // Upload attached photos (if any) to the same cliente
      if (photoFiles.length > 0 && form.cliente_id) {
        const cli = clientes.find((c) => c.id === form.cliente_id);
        const endereco = cli?.endereco || "";
        for (const pf of photoFiles) {
          try {
            await api.post("/fotos", {
              cliente_id: form.cliente_id,
              endereco,
              data: form.data,
              image_base64: pf.base64,
              descricao: pf.name || "",
            });
          } catch (err) {
            // continue with others
          }
        }
      }

      toast.success(t("common.success"));
      setOpen(false);
      setForm({ ...form, observacoes: "" });
      setPhotoFiles([]);
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const handlePhotoFiles = (e) => {
    const files = Array.from(e.target.files || []);
    const maxSize = 8 * 1024 * 1024;
    const valid = files.filter((f) => {
      if (f.size > maxSize) {
        toast.error(`${f.name}: max 8MB`);
        return false;
      }
      return true;
    });
    Promise.all(
      valid.map(
        (file) =>
          new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (ev) => resolve({ name: file.name, base64: ev.target.result });
            reader.readAsDataURL(file);
          })
      )
    ).then((results) => setPhotoFiles((prev) => [...prev, ...results]));
  };

  const removePhoto = (idx) => setPhotoFiles((prev) => prev.filter((_, i) => i !== idx));

  const handleDelete = async (id) => {
    if (!window.confirm(t("horas.confirmDelete"))) return;
    try {
      await api.delete(`/horas/${id}`);
      toast.success(t("common.success"));
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const getClientName = (cid) => clientes.find((c) => c.id === cid)?.nome || "—";
  const getFuncName = (fid) => funcionarios.find((f) => f.id === fid)?.name || "—";

  const statusBadge = (s) => {
    const map = {
      pendente: { bg: "bg-amber-100 text-amber-800", label: t("horas.pending") },
      aprobado: { bg: "bg-green-100 text-green-800", label: t("horas.approved") },
      aprovado: { bg: "bg-green-100 text-green-800", label: t("horas.approved") },
      rejeitado: { bg: "bg-red-100 text-red-800", label: t("horas.rejected") },
      rechazado: { bg: "bg-red-100 text-red-800", label: t("horas.rejected") },
      pagada: { bg: "bg-blue-100 text-blue-800", label: t("horas.paid") },
      cerrado: { bg: "bg-blue-100 text-blue-800", label: t("horas.paid") },
    };
    const m = map[s] || map.pendente;
    return <Badge className={`${m.bg} border-0 font-medium`}>{m.label}</Badge>;
  };

  return (
    <div data-testid="horas-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-3xl heading-font font-bold text-slate-900">{t("horas.title")}</h1>
          <p className="text-slate-500 mt-1 text-sm">{t("horas.subtitle")}</p>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-green-600 hover:bg-green-700 text-white gap-2" data-testid="add-horas-btn">
              <Plus size={18} weight="bold" /> {t("horas.add")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{t("horas.add")}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>{t("horas.date")}</Label>
                <Input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} required data-testid="horas-date-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("horas.start")}</Label>
                  <Input type="time" value={form.hora_inicio} onChange={(e) => setForm({ ...form, hora_inicio: e.target.value })} required data-testid="horas-start-input" />
                </div>
                <div>
                  <Label>{t("horas.end")}</Label>
                  <Input type="time" value={form.hora_fim} onChange={(e) => setForm({ ...form, hora_fim: e.target.value })} required data-testid="horas-end-input" />
                </div>
              </div>
              <div>
                <Label>{t("horas.lunch")}</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  value={form.almuerzo_horas}
                  onChange={(e) => setForm({ ...form, almuerzo_horas: e.target.value })}
                  data-testid="horas-lunch-input"
                />
                <p className="text-xs text-slate-500 mt-1">{t("horas.lunchHint")}</p>
              </div>
              <div>
                <Label>{t("horas.client")}</Label>
                <Select value={form.cliente_id} onValueChange={(v) => setForm({ ...form, cliente_id: v })}>
                  <SelectTrigger data-testid="horas-client-select"><SelectValue placeholder={t("horas.selectClient")} /></SelectTrigger>
                  <SelectContent>
                    {clientes.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("horas.notes")}</Label>
                <Textarea value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} rows={3} data-testid="horas-notes-input" />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <ImageIcon size={16} weight="duotone" /> {t("horas.photos")}
                </Label>
                <Input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoFiles}
                  data-testid="horas-photos-input"
                />
                <p className="text-xs text-slate-500 mt-1">{t("horas.photosHint")}</p>
                {photoFiles.length > 0 && (
                  <div className="grid grid-cols-4 gap-2 mt-3">
                    {photoFiles.map((pf, idx) => (
                      <div key={idx} className="relative group">
                        <img src={pf.base64} alt={pf.name} className="w-full h-20 object-cover rounded border border-slate-200" />
                        <button
                          type="button"
                          onClick={() => removePhoto(idx)}
                          className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`remove-photo-${idx}`}
                        >
                          <X size={12} weight="bold" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>{t("horas.cancel")}</Button>
                <Button type="submit" className="bg-green-600 hover:bg-green-700" data-testid="horas-save-btn">{t("horas.save")}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-4 py-3">{t("horas.date")}</th>
                {isAdmin && <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-4 py-3">{t("horas.employee")}</th>}
                <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-4 py-3">{t("horas.client")}</th>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-4 py-3">{t("horas.start")}</th>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-4 py-3">{t("horas.end")}</th>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-4 py-3">{t("horas.total")}</th>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-4 py-3">{t("horas.status")}</th>
                <th className="text-right text-xs font-bold uppercase tracking-wider text-slate-500 px-4 py-3">{t("horas.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="p-8 text-center text-slate-400">{t("common.loading")}</td></tr>
              ) : registros.length === 0 ? (
                <tr><td colSpan={8} className="p-10 text-center text-slate-400">
                  <Clock size={40} weight="duotone" className="mx-auto mb-2 text-slate-300" />
                  {t("horas.empty")}
                </td></tr>
              ) : registros.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/70" data-testid={`horas-row-${r.id}`}>
                  <td className="px-4 py-3 text-sm text-slate-700">{r.data}</td>
                  {isAdmin && <td className="px-4 py-3 text-sm text-slate-700">{getFuncName(r.funcionario_id)}</td>}
                  <td className="px-4 py-3 text-sm text-slate-700">{getClientName(r.cliente_id)}</td>
                  <td className="px-4 py-3 text-sm text-slate-700">{r.hora_inicio}</td>
                  <td className="px-4 py-3 text-sm text-slate-700">{r.hora_fim}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-slate-900">{r.total_horas} h</td>
                  <td className="px-4 py-3">{statusBadge(r.status)}</td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(r.id)} data-testid={`delete-horas-${r.id}`}>
                      <Trash size={16} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
